import React, { useState, useEffect, useCallback, useRef } from 'react';
import { COLORS } from '../App';
import SourceDocViewer from './SourceDocViewer';

const API_BASE = window.location.port === '3000' ? 'http://localhost:4100/api' : window.location.pathname.startsWith('/cmbs') ? '/cmbs/api' : '/api';

function apiFetch(path, opts = {}) {
  const token = localStorage.getItem('cmbs_token');
  const headers = { ...opts.headers };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  if (opts.body && !(opts.body instanceof FormData)) {
    headers['Content-Type'] = 'application/json';
    opts.body = JSON.stringify(opts.body);
  }
  return fetch(`${API_BASE}${path}`, { ...opts, headers }).then(r => r.json());
}

const FILE_CATEGORIES = [
  { key: 'sizer', label: 'Broker Sizer / UW Model', icon: '\u{1F4C8}', accept: '.xlsx,.xls,.csv', required: true },
  { key: 'rent_roll', label: 'Rent Roll', icon: '\u{1F3E0}', accept: '.xlsx,.xls,.csv,.pdf', required: true },
  { key: 't12', label: 'Trailing 12 Financials', icon: '\u{1F4CA}', accept: '.xlsx,.xls,.csv,.pdf', required: true },
  { key: 'historicals', label: 'Historical Financials', icon: '\u{1F4C5}', accept: '.xlsx,.xls,.csv,.pdf', required: true },
  { key: 'budget', label: '2026 Budget', icon: '\u{1F4B5}', accept: '.xlsx,.xls,.csv,.pdf', required: false },
  { key: 'offering_memo', label: 'Offering Memorandum', icon: '\u{1F4D6}', accept: '.pdf', required: false },
];

const CONFIDENCE = {
  high: { bg: '#E8F5E9', text: '#2E7D32', border: '#4CAF50', label: 'High Confidence' },
  medium: { bg: '#FFF8E1', text: '#F57F17', border: '#FFC107', label: 'Review Recommended' },
  low: { bg: '#FFEBEE', text: '#C62828', border: '#F44336', label: 'Low Confidence' },
};

function getConfLevel(score) {
  if (score >= 95) return 'high';
  if (score >= 70) return 'medium';
  return 'low';
}

function formatValue(val, dataType) {
  if (val == null || val === '') return '';
  if (dataType === 'currency') {
    const num = parseFloat(String(val).replace(/[$,]/g, ''));
    return isNaN(num) ? val : '$' + num.toLocaleString('en-US', { maximumFractionDigits: 0 });
  }
  if (dataType === 'percentage') {
    const num = parseFloat(String(val).replace(/%/g, ''));
    return isNaN(num) ? val : num.toFixed(2) + '%';
  }
  return String(val);
}

function formatSize(bytes) {
  if (!bytes) return '';
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(0) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
}

export default function ExtractionReview({ deal, onUpdate }) {
  const [phase, setPhase] = useState('upload'); // upload, extracting, review
  const [files, setFiles] = useState({}); // { category: fileRecord }
  const [allFiles, setAllFiles] = useState([]);
  const [fields, setFields] = useState([]);
  const [summary, setSummary] = useState(null);
  const [filter, setFilter] = useState('all');
  const [selectedField, setSelectedField] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editVal, setEditVal] = useState('');
  const [editNotes, setEditNotes] = useState('');
  const [logLines, setLogLines] = useState([]);
  const [fileProgress, setFileProgress] = useState({ current: 0, total: 0 });
  const [extractionDone, setExtractionDone] = useState(false);
  const [sourceViewer, setSourceViewer] = useState(null);
  const [loading, setLoading] = useState(true);
  const logEndRef = useRef(null);
  const eventSourceRef = useRef(null);

  // Restore state from backend on mount
  useEffect(() => { restoreState(); return () => { if (eventSourceRef.current) eventSourceRef.current.close(); }; }, [deal.id]);

  useEffect(() => { logEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [logLines]);

  const restoreState = async () => {
    setLoading(true);

    // 1. Check if extraction results exist
    const fieldsRes = await apiFetch(`/extraction/fields/${deal.id}?sort=confidence_asc`);
    if (fieldsRes.success && fieldsRes.data.fields.length > 0) {
      setFields(fieldsRes.data.fields);
      setSummary(fieldsRes.data.summary);

      // Also load files
      const filesRes = await apiFetch(`/upload/${deal.id}`);
      if (filesRes.success) {
        setAllFiles(filesRes.data);
        const byCategory = {};
        for (const f of filesRes.data) byCategory[f.file_category] = f;
        setFiles(byCategory);
      }

      setPhase('review');
      setLoading(false);
      return;
    }

    // 2. Check if extraction is running
    const statusRes = await apiFetch(`/extraction/status/${deal.id}`);
    if (statusRes.success && statusRes.data.status === 'running') {
      setPhase('extracting');
      setLoading(false);
      connectToStream();
      return;
    }

    // 3. Load files for upload phase
    const filesRes = await apiFetch(`/upload/${deal.id}`);
    if (filesRes.success) {
      setAllFiles(filesRes.data);
      const byCategory = {};
      for (const f of filesRes.data) byCategory[f.file_category] = f;
      setFiles(byCategory);
    }

    setPhase('upload');
    setLoading(false);
  };

  const connectToStream = () => {
    if (eventSourceRef.current) eventSourceRef.current.close();
    const token = localStorage.getItem('cmbs_token');
    const es = new EventSource(`${API_BASE}/extraction/stream/${deal.id}?token=${token}`);
    eventSourceRef.current = es;

    es.onmessage = (event) => {
      const data = JSON.parse(event.data);

      if (data.type === 'already_complete') { es.close(); restoreState(); return; }
      if (data.type === 'not_started') { es.close(); setPhase('upload'); return; }

      const time = data.timestamp
        ? new Date(data.timestamp).toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' })
        : new Date().toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' });

      // Thinking events: replace the previous thinking line instead of adding new ones
      if (data.type === 'thinking') {
        setLogLines(prev => [...prev.filter(l => l.type !== 'thinking'), { time, ...data }]);
        return;
      }
      // When a real event arrives, remove any lingering thinking line
      setLogLines(prev => [...prev.filter(l => l.type !== 'thinking'), { time, ...data }]);
      if (data.fileIndex && data.fileCount) setFileProgress({ current: data.fileIndex, total: data.fileCount });

      if (data.type === 'complete') {
        es.close();
        setExtractionDone(true);
        apiFetch(`/extraction/fields/${deal.id}?sort=confidence_asc`).then(res => {
          if (res.success) { setFields(res.data.fields); setSummary(res.data.summary); }
        });
        onUpdate();
      }
      if (data.type === 'error' && !data.fileName) es.close();
    };

    es.onerror = () => { es.close(); setTimeout(restoreState, 2000); };
  };

  const handleFileSelect = async (category, file) => {
    const fd = new FormData();
    fd.append('category', category);
    fd.append('file', file);
    const token = localStorage.getItem('cmbs_token');
    const res = await fetch(`${API_BASE}/upload/${deal.id}`, {
      method: 'POST', body: fd, headers: { 'Authorization': `Bearer ${token}` },
    }).then(r => r.json());
    if (res.success) {
      setFiles({ ...files, [category]: res.data });
      setAllFiles([...allFiles.filter(f => f.file_category !== category), res.data]);
    } else {
      alert(res.error || 'Upload failed');
    }
  };

  const handleRemoveFile = async (category) => {
    const file = files[category];
    if (!file) return;
    await apiFetch(`/upload/${deal.id}/files/${file.id}`, { method: 'DELETE' });
    const newFiles = { ...files };
    delete newFiles[category];
    setFiles(newFiles);
    setAllFiles(allFiles.filter(f => f.id !== file.id));
  };

  const handleRunExtraction = async () => {
    setPhase('extracting');
    setLogLines([]);
    setFileProgress({ current: 0, total: 0 });
    setExtractionDone(false);

    // Start extraction on the server (returns immediately)
    await apiFetch(`/extraction/run-all/${deal.id}`, { method: 'POST' });

    // Connect to SSE stream to watch progress
    connectToStream();
  };

  const handleViewResults = () => { setPhase('review'); };

  const handleApprove = async (id) => {
    await apiFetch(`/review/approve/${id}`, { method: 'PUT' });
    await reloadFields();
  };

  const handleEdit = async (id) => {
    await apiFetch(`/review/edit/${id}`, { method: 'PUT', body: { edited_value: editVal, notes: editNotes } });
    setEditingId(null);
    await reloadFields();
  };

  const handleReject = async (id) => {
    await apiFetch(`/review/reject/${id}`, { method: 'PUT', body: { notes: '' } });
    await reloadFields();
  };

  const handleBulkApprove = async () => {
    await apiFetch(`/review/approve-high/${deal.id}`, { method: 'POST', body: { threshold: 95 } });
    await reloadFields();
    onUpdate();
  };

  const reloadFields = async () => {
    const res = await apiFetch(`/extraction/fields/${deal.id}?sort=confidence_asc`);
    if (res.success) { setFields(res.data.fields); setSummary(res.data.summary); }
  };

  const uploadedCount = Object.keys(files).length;
  const missingRequired = FILE_CATEGORIES.filter(c => c.required && !files[c.key]);

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: COLORS.textLight }}>Loading...</div>;

  // ==================== PHASE 1: UPLOAD ====================
  if (phase === 'upload') {
    return (
      <div>
        <h3 style={{ fontFamily: 'Georgia, serif', color: COLORS.navy, marginBottom: 6 }}>Upload Broker Files</h3>
        <p style={{ color: COLORS.textLight, fontSize: 13, marginBottom: 24 }}>
          Drag and drop or browse for each file the broker provided. Required files are marked with *. The system will extract data from these files and map it into the blank underwriting model.
        </p>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 24 }}>
          {FILE_CATEGORIES.map(cat => (
            <UploadZone
              key={cat.key}
              category={cat}
              file={files[cat.key]}
              onFileSelect={(file) => handleFileSelect(cat.key, file)}
              onRemove={() => handleRemoveFile(cat.key)}
            />
          ))}
        </div>

        {/* Status line */}
        <div style={{ marginBottom: 20, fontSize: 13 }}>
          <span style={{ color: COLORS.navy, fontWeight: 600 }}>{uploadedCount} of {FILE_CATEGORIES.length} files uploaded</span>
          {missingRequired.length > 0 && (
            <span style={{ marginLeft: 16, color: '#F57F17' }}>
              Missing required: {missingRequired.map(c => c.label).join(', ')}
            </span>
          )}
        </div>

        {/* Run Extraction button */}
        <button
          onClick={handleRunExtraction}
          disabled={uploadedCount === 0}
          style={{
            padding: '14px 32px', borderRadius: 8, border: 'none', fontSize: 15, fontWeight: 700, cursor: uploadedCount > 0 ? 'pointer' : 'not-allowed',
            background: uploadedCount > 0 ? COLORS.gold : '#CCCCCC',
            color: uploadedCount > 0 ? COLORS.navy : '#666',
          }}
        >
          Run Extraction
        </button>
      </div>
    );
  }

  // ==================== EXTRACTING ====================
  if (phase === 'extracting') {
    const pct = fileProgress.total > 0 ? Math.round((fileProgress.current / fileProgress.total) * 100) : 0;
    return (
      <div style={{ background: '#fff', borderRadius: 10, border: '1px solid #E2E6EC', overflow: 'hidden' }}>
        {/* Header */}
        <div style={{ padding: '16px 24px', borderBottom: '1px solid #E2E6EC' }}>
          <h3 style={{ fontFamily: 'Georgia, serif', color: COLORS.navy, marginBottom: 4 }}>Extraction in Progress</h3>
          <span style={{ fontSize: 13, color: COLORS.textLight }}>{deal.property_name} | {fileProgress.total || uploadedCount} files</span>
        </div>

        {/* Terminal log */}
        <div style={{ background: '#0F1B2D', padding: 20, maxHeight: 'calc(100vh - 380px)', overflowY: 'auto', fontFamily: "'Consolas', 'Fira Code', monospace", fontSize: 13, lineHeight: 1.7 }}>
          <style>{`@keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.3; } }`}</style>
          {logLines.map((line, i) => {
            if (line.type === 'thinking') {
              return (
                <div key="thinking" style={{ color: '#C9A84C', marginBottom: 1, display: 'flex', alignItems: 'center', gap: 6, fontStyle: 'italic' }}>
                  <span style={{ color: '#6B7A8D', marginRight: 10 }}>[{line.time}]</span>
                  <span style={{ color: '#555' }}>{'\u2502'}  </span>
                  <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: '#C9A84C', animation: 'pulse 1.5s ease-in-out infinite' }} />
                  <span>{line.message}</span>
                </div>
              );
            }

            const isFile = line.type === 'file_start';
            const isComplete = line.type === 'file_complete' || line.type === 'complete';
            const isField = line.type === 'field_detail';
            const isError = line.type === 'error';
            const isSub = ['sheet_scan', 'sheet_data', 'extraction_start', 'fields_extracted', 'field_detail', 'extraction_chunk', 'extraction_batch_complete', 'extraction_warning'].includes(line.type);
            const nextLine = logLines[i + 1];
            const isLast = isSub && (!nextLine || nextLine.type === 'file_complete' || nextLine.type === 'file_start' || nextLine.type === 'complete');
            const prefix = isSub ? (isLast ? '\u2514\u2500 ' : '\u251C\u2500 ') : '';

            return (
              <div key={i} style={{ color: isError ? '#EF5350' : isComplete ? '#4CAF50' : isFile ? '#FFFFFF' : isField ? '#C9A84C' : '#A8B8C8', marginBottom: 1, fontWeight: isFile || isComplete ? 600 : 400 }}>
                <span style={{ color: '#6B7A8D', marginRight: 10 }}>[{line.time}]</span>
                <span style={{ color: '#555' }}>{prefix}</span>
                {isComplete && '\u2713 '}
                {line.message}
              </div>
            );
          })}
          <div ref={logEndRef} />
        </div>

        {/* Progress bar */}
        <div style={{ padding: '12px 24px', borderTop: '1px solid #E2E6EC', display: 'flex', alignItems: 'center', gap: 16 }}>
          <div style={{ flex: 1, height: 8, background: '#E9ECEF', borderRadius: 4, overflow: 'hidden' }}>
            <div style={{ height: '100%', background: extractionDone ? COLORS.success : COLORS.gold, borderRadius: 4, width: `${extractionDone ? 100 : pct}%`, transition: 'width 0.3s' }} />
          </div>
          <span style={{ fontSize: 12, color: COLORS.textLight, flexShrink: 0 }}>
            {extractionDone ? 'Complete' : `${fileProgress.current}/${fileProgress.total} files`}
          </span>
          {extractionDone && (
            <button onClick={handleViewResults}
              style={{ padding: '10px 24px', background: COLORS.gold, color: COLORS.navy, border: 'none', borderRadius: 6, fontWeight: 700, cursor: 'pointer', fontSize: 14, flexShrink: 0 }}>
              View Results
            </button>
          )}
        </div>
      </div>
    );
  }

  // ==================== PHASE 2: REVIEW ====================
  const filtered = fields.filter(f => {
    if (filter === 'all') return true;
    if (filter === 'needs_review') return f.confidence_score < 95 && f.analyst_status === 'pending';
    if (filter === 'low') return f.confidence_score < 70;
    return f.analyst_status === filter;
  });

  const grouped = {};
  for (const f of filtered) {
    const cat = f.field_category || 'other';
    if (!grouped[cat]) grouped[cat] = [];
    grouped[cat].push(f);
  }

  const s = {
    panel: { background: COLORS.white, borderRadius: 8, border: `1px solid ${COLORS.border}`, overflow: 'hidden' },
    sectionHeader: { fontFamily: 'Georgia, serif', fontSize: 16, color: COLORS.navy, padding: '12px 16px', borderBottom: `1px solid ${COLORS.border}`, background: '#FAFBFC' },
  };

  return (
    <div>
      <h3 style={{ fontFamily: 'Georgia, serif', color: COLORS.navy, marginBottom: 16 }}>Data Extraction Review</h3>

      {/* Summary Bar */}
      {summary && (
        <div style={{ ...s.panel, padding: 20, marginBottom: 16, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
          <div style={{ display: 'flex', gap: 20, alignItems: 'center' }}>
            <Stat label="Total Fields" value={summary.total} />
            <Stat label="High Confidence" value={summary.high} color="#2E7D32" />
            <Stat label="Needs Review" value={summary.medium} color="#F57F17" />
            <Stat label="Low Confidence" value={summary.low} color="#C62828" />
            <Stat label="Approved" value={parseInt(summary.approved || 0) + parseInt(summary.edited || 0)} color="#1565C0" />
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={() => setPhase('upload')} style={btnStyle('#fff', COLORS.navy, false, true)}>Back to Upload</button>
            {parseInt(summary.high || 0) > 0 && parseInt(summary.pending || 0) > 0 && (
              <button onClick={handleBulkApprove} style={btnStyle(COLORS.success, '#fff')}>Approve All High Confidence</button>
            )}
          </div>
        </div>
      )}

      {/* Progress Bar */}
      {summary && parseInt(summary.total) > 0 && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
            <span style={{ fontSize: 12, color: COLORS.textLight }}>Review Progress</span>
            <span style={{ fontSize: 12, fontWeight: 600, color: COLORS.navy }}>
              {parseInt(summary.approved || 0) + parseInt(summary.edited || 0)} / {summary.total} reviewed
            </span>
          </div>
          <div style={{ height: 8, background: '#E9ECEF', borderRadius: 4, overflow: 'hidden' }}>
            <div style={{ height: '100%', background: COLORS.gold, borderRadius: 4, width: `${((parseInt(summary.approved || 0) + parseInt(summary.edited || 0)) / parseInt(summary.total)) * 100}%`, transition: 'width 0.3s' }} />
          </div>
        </div>
      )}

      {/* Filter Toggles */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 16, flexWrap: 'wrap' }}>
        {[['all', 'All'], ['needs_review', 'Needs Review'], ['low', 'Low Confidence'], ['approved', 'Approved'], ['edited', 'Edited'], ['rejected', 'Rejected']].map(([key, label]) => (
          <button key={key} onClick={() => setFilter(key)}
            style={{ padding: '6px 14px', borderRadius: 4, border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 600,
              background: filter === key ? COLORS.navy : '#E9ECEF', color: filter === key ? '#fff' : COLORS.text }}>
            {label}
          </button>
        ))}
      </div>

      {/* Three Panel Layout */}
      <div style={{ display: 'grid', gridTemplateColumns: '30% 45% 25%', gap: 12, alignItems: 'start' }}>
        {/* LEFT: Source Documents */}
        <div style={{ ...s.panel, maxHeight: 'calc(100vh - 340px)', overflow: 'auto' }}>
          <div style={s.sectionHeader}>Source Documents</div>
          {allFiles.length === 0 ? (
            <div style={{ padding: 20, textAlign: 'center', color: COLORS.textLight, fontSize: 13 }}>No files uploaded yet</div>
          ) : allFiles.map(f => (
            <div key={f.id} style={{ padding: '10px 16px', borderBottom: `1px solid ${COLORS.border}`, fontSize: 13 }}>
              <div style={{ fontWeight: 600, color: COLORS.navy, marginBottom: 2 }}>{f.file_name}</div>
              <div style={{ fontSize: 11, color: COLORS.textLight }}>
                {(f.file_category || '').replace(/_/g, ' ').toUpperCase()} | {formatSize(f.file_size)}
                {f.parsed_at && ' | Extracted'}
              </div>
              {selectedField && selectedField.source_file_id === f.id && (
                <div style={{ marginTop: 8, padding: 8, background: '#FFF9C4', borderRadius: 4, fontSize: 11, fontFamily: 'monospace' }}>
                  <div><strong>Sheet:</strong> {selectedField.source_sheet_name}</div>
                  <div><strong>Cell:</strong> {selectedField.source_cell_ref}</div>
                  {selectedField.source_row_context && <div style={{ marginTop: 4, wordBreak: 'break-all' }}><strong>Row:</strong> {selectedField.source_row_context}</div>}
                  {selectedField.source_file_id && (
                    <button onClick={() => setSourceViewer({ fileId: selectedField.source_file_id, sheet: selectedField.source_sheet_name, cell: selectedField.source_cell_ref })}
                      style={{ marginTop: 6, padding: '4px 10px', background: COLORS.navy, color: '#fff', border: 'none', borderRadius: 4, fontSize: 10, cursor: 'pointer' }}>
                      View Source
                    </button>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* CENTER: Extracted Fields */}
        <div style={{ maxHeight: 'calc(100vh - 340px)', overflow: 'auto' }}>
          {Object.keys(grouped).length === 0 ? (
            <div style={{ ...s.panel, padding: 40, textAlign: 'center', color: COLORS.textLight }}>
              {parseInt(summary?.total || 0) === 0 ? 'No extraction results yet.' : 'No fields match the current filter.'}
            </div>
          ) : Object.entries(grouped).map(([category, catFields]) => (
            <div key={category} style={{ ...s.panel, marginBottom: 12 }}>
              <div style={s.sectionHeader}>
                {category.replace(/_/g, ' ').toUpperCase()}
                <span style={{ fontSize: 12, color: COLORS.textLight, marginLeft: 8 }}>({catFields.length})</span>
              </div>
              {catFields.map(field => {
                const conf = getConfLevel(field.confidence_score);
                const cs = CONFIDENCE[conf];
                const isEditing = editingId === field.id;
                const isApproved = field.analyst_status === 'approved' || field.analyst_status === 'edited';
                const isRejected = field.analyst_status === 'rejected';

                return (
                  <div key={field.id} onClick={() => setSelectedField(field)}
                    style={{ padding: '12px 16px', borderBottom: `1px solid ${COLORS.border}`, cursor: 'pointer',
                      borderLeft: `3px solid ${isApproved ? '#4CAF50' : isRejected ? '#F44336' : cs.border}`,
                      background: isApproved ? '#F1F8E910' : isRejected ? '#FFEBEE20' : selectedField?.id === field.id ? '#F5F5F5' : 'transparent' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 13, color: '#333', fontWeight: 500 }}>{field.field_name}</div>
                        <div style={{ fontSize: 14, fontWeight: 700, color: '#1A2332', marginTop: 2 }}>
                          {formatValue(field.analyst_edited_value || field.extracted_value, field.data_type)}
                        </div>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                        <span style={{ padding: '2px 8px', borderRadius: 10, fontSize: 10, fontWeight: 700, background: cs.bg, color: cs.text }}>
                          {Math.round(field.confidence_score)}%
                        </span>
                        {field.analyst_status !== 'pending' && (
                          <span style={{ padding: '2px 8px', borderRadius: 10, fontSize: 10, fontWeight: 600,
                            background: isApproved ? '#E8F5E9' : '#FFEBEE', color: isApproved ? '#2E7D32' : '#C62828' }}>
                            {field.analyst_status}
                          </span>
                        )}
                      </div>
                    </div>
                    <div style={{ fontSize: 11, color: COLORS.textLight, marginBottom: 6 }}
                      onClick={(e) => { e.stopPropagation(); if (field.source_file_id) setSourceViewer({ fileId: field.source_file_id, sheet: field.source_sheet_name, cell: field.source_cell_ref }); }}>
                      <span style={{ color: COLORS.navy, textDecoration: 'underline', cursor: 'pointer' }}>{field.source_file_name}</span>
                      {field.source_sheet_name && ` > ${field.source_sheet_name}`}
                      {field.source_cell_ref && ` > ${field.source_cell_ref}`}
                      {field.extraction_method && (
                        <span style={{ marginLeft: 8, padding: '1px 6px', borderRadius: 3, background: '#E3F2FD', color: '#1565C0', fontSize: 10 }}>
                          {field.extraction_method.replace(/_/g, ' ')}
                        </span>
                      )}
                    </div>
                    {!isEditing && field.analyst_status === 'pending' && (
                      <div style={{ display: 'flex', gap: 6, marginTop: 4 }} onClick={e => e.stopPropagation()}>
                        <button onClick={() => handleApprove(field.id)} style={smallBtn('#4CAF50', '#fff')}>Approve</button>
                        <button onClick={() => { setEditingId(field.id); setEditVal(field.extracted_value); setEditNotes(''); }} style={smallBtn('#fff', '#333', true)}>Edit</button>
                        <button onClick={() => handleReject(field.id)} style={smallBtn('#fff', '#C62828', true)}>Reject</button>
                      </div>
                    )}
                    {isEditing && (
                      <div style={{ marginTop: 6 }} onClick={e => e.stopPropagation()}>
                        <input value={editVal} onChange={e => setEditVal(e.target.value)}
                          style={{ width: '100%', padding: '6px 10px', border: `1px solid ${COLORS.border}`, borderRadius: 4, fontSize: 13, marginBottom: 4 }} />
                        <input value={editNotes} onChange={e => setEditNotes(e.target.value)} placeholder="Reason for edit (optional)"
                          style={{ width: '100%', padding: '6px 10px', border: `1px solid ${COLORS.border}`, borderRadius: 4, fontSize: 12, marginBottom: 4 }} />
                        <div style={{ display: 'flex', gap: 6 }}>
                          <button onClick={() => handleEdit(field.id)} style={smallBtn('#1565C0', '#fff')}>Save</button>
                          <button onClick={() => setEditingId(null)} style={smallBtn('#fff', '#333', true)}>Cancel</button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </div>

        {/* RIGHT: Target Model Preview */}
        <div style={{ ...s.panel, maxHeight: 'calc(100vh - 340px)', overflow: 'auto' }}>
          <div style={s.sectionHeader}>UW Model Mapping</div>
          {fields.filter(f => f.target_model_tab).length === 0 ? (
            <div style={{ padding: 20, textAlign: 'center', color: COLORS.textLight, fontSize: 12 }}>
              {deal.template_id ? 'Fields will map to the model after extraction.' : 'Assign a UW template to enable model mapping.'}
            </div>
          ) : (() => {
            const byTab = {};
            for (const f of fields) {
              if (!f.target_model_tab) continue;
              if (!byTab[f.target_model_tab]) byTab[f.target_model_tab] = [];
              byTab[f.target_model_tab].push(f);
            }
            return Object.entries(byTab).map(([tab, tabFields]) => (
              <div key={tab}>
                <div style={{ padding: '8px 16px', background: '#F5F5F5', fontSize: 12, fontWeight: 600, color: COLORS.navy, borderBottom: `1px solid ${COLORS.border}` }}>{tab}</div>
                {tabFields.map(f => {
                  const isApproved = f.analyst_status === 'approved' || f.analyst_status === 'edited';
                  const isPending = f.analyst_status === 'pending';
                  return (
                    <div key={f.id} style={{ padding: '6px 16px', borderBottom: '1px solid #F0F0F0', fontSize: 11, display: 'flex', justifyContent: 'space-between',
                      background: isApproved ? '#E8F5E920' : 'transparent' }}>
                      <span style={{ color: COLORS.textLight }}>{f.target_cell_ref}: {f.field_name}</span>
                      <span style={{ fontWeight: 600, fontSize: 10, color: isApproved ? '#2E7D32' : isPending ? '#F57F17' : '#C62828' }}>
                        {isApproved ? formatValue(f.analyst_edited_value || f.extracted_value, f.data_type) : isPending ? '...' : 'X'}
                      </span>
                    </div>
                  );
                })}
              </div>
            ));
          })()}
        </div>
      </div>

      {sourceViewer && (
        <SourceDocViewer
          dealId={deal.id}
          fileId={sourceViewer.fileId}
          sheet={sourceViewer.sheet}
          highlightCell={sourceViewer.cell}
          onClose={() => setSourceViewer(null)}
        />
      )}
    </div>
  );
}

// ==================== Upload Zone Component ====================
function UploadZone({ category, file, onFileSelect, onRemove }) {
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef();

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const dropped = e.dataTransfer.files[0];
    if (dropped) onFileSelect(dropped);
  };

  const handleClick = () => {
    if (file) return;
    inputRef.current.click();
  };

  const handleChange = (e) => {
    const selected = e.target.files[0];
    if (selected) onFileSelect(selected);
    e.target.value = '';
  };

  const hasFile = !!file;
  const borderColor = hasFile ? '#4CAF50' : dragOver ? '#C9A84C' : '#E2E6EC';
  const bgColor = hasFile ? '#F1F8E9' : dragOver ? '#FFF8E1' : '#FFFFFF';

  return (
    <div
      onDragOver={(e) => { e.preventDefault(); if (!hasFile) setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={hasFile ? undefined : handleDrop}
      onClick={handleClick}
      style={{
        border: `2px dashed ${borderColor}`, borderRadius: 10, padding: '16px 20px',
        background: bgColor, cursor: hasFile ? 'default' : 'pointer',
        display: 'flex', alignItems: 'center', gap: 14, transition: 'all 0.15s', minHeight: 80,
      }}
    >
      <input ref={inputRef} type="file" accept={category.accept} onChange={handleChange} style={{ display: 'none' }} />

      <div style={{ fontSize: 28, flexShrink: 0 }}>{hasFile ? '\u2705' : category.icon}</div>

      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: COLORS.navy }}>
          {category.label}{category.required && <span style={{ color: '#C62828', marginLeft: 4 }}>*</span>}
        </div>
        {hasFile ? (
          <div style={{ fontSize: 12, color: '#2E7D32', marginTop: 2 }}>
            {file.file_name} ({formatSize(file.file_size)})
          </div>
        ) : (
          <div style={{ fontSize: 12, color: COLORS.textLight, marginTop: 2 }}>Drop file here or click to browse</div>
        )}
      </div>

      {hasFile && (
        <button onClick={(e) => { e.stopPropagation(); onRemove(); }}
          style={{ width: 28, height: 28, borderRadius: '50%', border: `1px solid ${COLORS.border}`, background: '#fff',
            cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, color: '#999', flexShrink: 0 }}>
          X
        </button>
      )}
    </div>
  );
}

function Stat({ label, value, color }) {
  return (
    <div style={{ textAlign: 'center' }}>
      <div style={{ fontSize: 22, fontWeight: 700, color: color || '#1A2332' }}>{value}</div>
      <div style={{ fontSize: 11, color: '#6C757D' }}>{label}</div>
    </div>
  );
}

function btnStyle(bg, color, disabled, bordered) {
  return { padding: '8px 18px', background: bg, color, border: bordered ? `1px solid ${COLORS.border}` : 'none', borderRadius: 4, fontWeight: 600, cursor: 'pointer', fontSize: 13, opacity: disabled ? 0.6 : 1 };
}

function smallBtn(bg, color, bordered) {
  return { padding: '4px 10px', background: bg, color, border: bordered ? `1px solid ${COLORS.border}` : 'none', borderRadius: 4, fontSize: 11, cursor: 'pointer', fontWeight: 500 };
}
